import { useEffect, useRef, useState } from "react";

/**
 * Subscribes to the AEGIS Server-Sent Events stream and exposes the
 * most recent events + the latest network metrics derived from burn
 * events. Auto-reconnects on transport drops (native EventSource
 * behaviour) and tolerates the SSE replay buffer the backend sends on
 * connect so the ticker is never empty.
 *
 *   const { events, multiplier, totalBurned, circulating, connected } = useAegisStream();
 */
export default function useAegisStream(limit = 8) {
  const [events, setEvents] = useState([]);
  const [multiplier, setMultiplier] = useState(null);
  const [totalBurned, setTotalBurned] = useState(null);
  const [circulating, setCirculating] = useState(null);
  const [connected, setConnected] = useState(false);
  const sourceRef = useRef(null);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/aegis/stream`;
    const es = new EventSource(url);
    sourceRef.current = es;
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);

    const consume = (kind) => (msg) => {
      try {
        const evt = JSON.parse(msg.data);
        if (kind === "burn" && evt?.data) {
          if (evt.data.burn_multiplier != null) setMultiplier(evt.data.burn_multiplier);
          if (evt.data.total_burned_proof != null) setTotalBurned(evt.data.total_burned_proof);
          if (evt.data.circulating_proof != null) setCirculating(evt.data.circulating_proof);
        }
        setEvents((prev) => {
          // Skip the synthetic "hello" event from showing in the ticker.
          if (evt.type === "hello") return prev;
          const next = [evt, ...prev];
          return next.slice(0, Math.max(1, limit));
        });
      } catch {
        // ignore malformed frames
      }
    };
    es.addEventListener("mint", consume("mint"));
    es.addEventListener("burn", consume("burn"));
    es.addEventListener("hello", consume("hello"));

    return () => {
      es.close();
      sourceRef.current = null;
    };
  }, [limit]);

  return { events, multiplier, totalBurned, circulating, connected };
}
